import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { Loading } from "../../components/Loading";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, LogOut, Users } from "lucide-react";
import { PinSetupCard } from "../../components/PinSetupCard";
import { RecentLoginsCard } from "../../components/RecentLoginsCard";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function OwnerSettings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
    api.get("/product-types").then((r) => setProductTypes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        company_name: settings.company_name, closing_time: settings.closing_time,
        working_days: settings.working_days, timezone: settings.timezone,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleDay = (d) => {
    const wd = settings.working_days.includes(d) ? settings.working_days.filter((x) => x !== d) : [...settings.working_days, d];
    setSettings({ ...settings, working_days: wd });
  };

  const addDesign = async () => {
    const name = prompt("Design name?");
    if (!name) return;
    try {
      await api.post("/designs", { design_name: name, standard_days: 5 });
      toast.success("Design added");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delDesign = async (id) => {
    if (!window.confirm("Remove this design?")) return;
    await api.delete(`/designs/${id}`);
    load();
  };

  const addProductType = async () => {
    const name = prompt("Product type name? (e.g. Bracelet)");
    if (!name || !name.trim()) return;
    try {
      await api.post("/product-types", { name: name.trim() });
      toast.success("Product type added");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delProductType = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Existing jobs will keep this product type.`)) return;
    await api.delete(`/product-types/${t.id}`);
    toast.success("Product type removed");
    load();
  };

  if (!settings) return (<><AppHeader title="Settings" subtitle="Owner" /><Loading /></>);
  const input = "w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#C86D51] outline-none text-base bg-white";

  return (
    <>
      <AppHeader title="Settings" subtitle="Owner" />
      <div className="px-4 lg:px-8 py-4 space-y-6 lg:max-w-3xl lg:mx-auto">
        <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <h2 className="font-display text-base font-bold">Company</h2>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Company Name</label>
            <input data-testid="settings-company" className={input} value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Daily Closing Time</label>
            <input data-testid="settings-closing-time" type="time" className={input} value={settings.closing_time || "19:00"} onChange={(e) => setSettings({ ...settings, closing_time: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">Working Days</label>
            <div className="flex gap-2 flex-wrap">
              {DAYS.map((d) => (
                <button key={d} onClick={() => toggleDay(d)}
                  className={`px-3 py-2 rounded-lg text-sm font-bold ${settings.working_days.includes(d) ? "copper-gradient text-white" : "bg-slate-100 text-slate-500"}`}>{d}</button>
              ))}
            </div>
          </div>
          <button data-testid="settings-save-button" onClick={save} disabled={saving} className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold">Design Master</h2>
            <button data-testid="add-design-button" onClick={addDesign} className="flex items-center gap-1 text-sm font-bold text-[#9A462B]"><Plus className="h-4 w-4" /> Add</button>
          </div>
          <div className="space-y-2">
            {designs.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{d.design_name}</p>
                  <p className="text-xs text-slate-400">{d.design_code} · {d.standard_days || "—"} days / 100 pcs</p>
                </div>
                <button onClick={() => delDesign(d.id)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display text-base font-bold">Product Types</h2>
            <button data-testid="add-product-type-button" onClick={addProductType} className="flex items-center gap-1 text-sm font-bold text-[#9A462B]"><Plus className="h-4 w-4" /> Add</button>
          </div>
          <p className="text-xs text-slate-400 mb-3">Used when creating jobs. Deleting a type does not change existing jobs.</p>
          <div className="space-y-2">
            {productTypes.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                <button data-testid={`delete-product-type-${t.name.toLowerCase()}`} onClick={() => delProductType(t)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {productTypes.length === 0 && <p className="text-sm text-slate-400">No product types yet.</p>}
          </div>
        </section>

        <button data-testid="manager-management-button" onClick={() => navigate("/owner/managers")} className="w-full flex items-center gap-3 bg-white rounded-2xl border border-slate-200 p-4 text-left active:scale-[0.99] transition-transform">
          <Users className="h-5 w-5 text-[#9A462B]" />
          <div>
            <p className="font-bold text-slate-800">Manager Management</p>
            <p className="text-xs text-slate-400">Add, edit, delete and force-logout managers</p>
          </div>
        </button>

        <button data-testid="audit-log-button" onClick={() => navigate("/owner/audit")} className="w-full flex items-center gap-3 bg-white rounded-2xl border border-slate-200 p-4 text-left active:scale-[0.99] transition-transform">
          <ClipboardList className="h-5 w-5 text-[#9A462B]" />
          <div>
            <p className="font-bold text-slate-800">Audit Log</p>
            <p className="text-xs text-slate-400">View all recorded actions</p>
          </div>
        </button>

        <PinSetupCard />

        <RecentLoginsCard />

        <button data-testid="owner-logout-button" onClick={logout} className="w-full flex items-center justify-center gap-2 bg-white rounded-2xl border border-slate-200 p-4 font-bold text-red-600 active:scale-[0.99] transition-transform lg:hidden">
          <LogOut className="h-5 w-5" /> Log Out
        </button>
      </div>
    </>
  );
}
